// Activiteiten uit IndexedDB (zelfde database als de oude app). Port van legacy initGameOn,
// loadActivities, saveActivitiesToDB en processImportedActivities.
// Standaardlijst = public/gameon_activiteiten.md (zie domain/defaultActivities.ts). Een niet-aangepaste lijst wordt
// alleen vernieuwd als de hash van die md verandert; een aangepaste lijst blijft altijd ongemoeid.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { mergeActivities, sanitizeActivityList } from '../domain/activities';
import { inputToActivity, nextActivityId, type ActivityInput } from '../domain/activityForm';
import { DEFAULT_ACTIVITIES, DEFAULTS_HASH } from '../domain/defaultActivities';
import type { Activity, ImportMode } from '../domain/types';
import { incrementActivityScore, isQuotaError, loadAllActivities, saveAllActivities } from '../storage/activityRepo';
import { KEYS } from '../storage/keys';
import { getFlag, getRaw, removeKey, setRaw } from '../storage/localStore';
import { applyLegacyRenames, defaultsOutdated, idsChanged, keepScoresByName, needsReseed } from './bootstrap';
import type { ActivitiesApi } from './contracts';
import { useToast } from './ToastProvider';

const ActivitiesContext = createContext<ActivitiesApi | null>(null);

const nameKey = (naam: string) => naam.trim().toLowerCase();

export function ActivitiesProvider({ children }: { children: ReactNode }) {
  const { showToast } = useToast();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [customized, setCustomized] = useState<boolean>(() => getFlag(KEYS.customDatasetLoaded));
  const listRef = useRef<Activity[]>([]);
  const started = useRef(false);

  const commit = useCallback((list: Activity[]) => {
    listRef.current = list;
    setActivities(list);
  }, []);

  /** Schoont op en schrijft weg; toast + throw bij fout (oude lijst blijft dan staan). */
  const persist = useCallback(
    async (list: Activity[]): Promise<Activity[]> => {
      const clean = sanitizeActivityList(list);
      try {
        await saveAllActivities(clean);
      } catch (e) {
        showToast(
          isQuotaError(e)
            ? 'Opslag vol: activiteiten konden niet worden bewaard. Je vorige lijst blijft behouden.'
            : 'Activiteiten opslaan mislukt. Je vorige lijst blijft behouden.',
          4500,
        );
        throw e;
      }
      commit(clean);
      return clean;
    },
    [commit, showToast],
  );

  const markCustomized = useCallback(() => {
    setRaw(KEYS.customDatasetLoaded, 'true');
    setCustomized(true);
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      let raw: unknown[] = [];
      try {
        raw = await loadAllActivities();
      } catch (e) {
        console.error('IndexedDB laadfout:', e);
      }
      let list = sanitizeActivityList(raw);
      let dirty = idsChanged(raw, list);
      const isCustom = getFlag(KEYS.customDatasetLoaded);
      let storeHash = false;

      if (needsReseed(list.length, isCustom)) {
        list = sanitizeActivityList(DEFAULT_ACTIVITIES);
        dirty = true;
        storeHash = true;
      } else if (defaultsOutdated(getRaw(KEYS.defaultsHash), DEFAULTS_HASH, isCustom)) {
        // De md met de standaardlijst is gewijzigd en de lijst is niet aangepast: vernieuwen, scores blijven op naam.
        list = sanitizeActivityList(keepScoresByName(DEFAULT_ACTIVITIES, list));
        dirty = true;
        storeHash = true;
      }
      const renamed = applyLegacyRenames(list);
      if (renamed.changed) {
        list = renamed.list;
        dirty = true;
      }
      commit(list);
      setLoading(false);
      if (dirty) {
        try {
          await saveAllActivities(list);
          if (storeHash) setRaw(KEYS.defaultsHash, DEFAULTS_HASH);
        } catch (e) {
          console.warn('Activiteiten konden niet worden bijgewerkt in IndexedDB:', e);
        }
      }
    })();
  }, [commit]);

  const importActivities = useCallback(
    async (raw: unknown[], mode: ImportMode): Promise<number> => {
      const incoming = sanitizeActivityList(Array.isArray(raw) ? raw : []);
      const current = listRef.current;
      const next = mode === 'overwrite' ? incoming : mergeActivities(current, incoming);
      const saved = await persist(next);
      markCustomized();
      removeKey(KEYS.lastReshuffleIds);
      return mode === 'overwrite' ? saved.length : Math.max(0, saved.length - current.length);
    },
    [persist, markCustomized],
  );

  const incrementScore = useCallback(async (id: number): Promise<void> => {
    try {
      const updated = await incrementActivityScore(id);
      if (!updated) return;
    } catch (e) {
      console.error('Kon score niet verhogen:', e);
      return;
    }
    const next = listRef.current.map((a) => (a.id === id ? { ...a, score: (a.score || 0) + 1 } : a));
    listRef.current = next;
    setActivities(next);
  }, []);

  const resetToDefaults = useCallback(async (): Promise<void> => {
    await persist(keepScoresByName(DEFAULT_ACTIVITIES, listRef.current));
    removeKey(KEYS.customDatasetLoaded);
    removeKey(KEYS.lastReshuffleIds);
    setRaw(KEYS.defaultsHash, DEFAULTS_HASH);
    setCustomized(false);
  }, [persist]);

  const addActivity = useCallback(
    async (input: ActivityInput): Promise<Activity> => {
      const current = listRef.current;
      const created = inputToActivity(input, nextActivityId(current), 0);
      await persist([...current, created]);
      markCustomized();
      return created;
    },
    [persist, markCustomized],
  );

  const updateActivity = useCallback(
    async (id: number, input: ActivityInput): Promise<Activity> => {
      const current = listRef.current;
      const old = current.find((a) => a.id === id);
      if (!old) throw new Error(`Activiteit ${id} bestaat niet`);
      const updated = inputToActivity(input, id, old.score);
      await persist(current.map((a) => (a.id === id ? updated : a)));
      markCustomized();
      return updated;
    },
    [persist, markCustomized],
  );

  const deleteActivity = useCallback(
    async (id: number): Promise<void> => {
      await persist(listRef.current.filter((a) => a.id !== id));
      markCustomized();
    },
    [persist, markCustomized],
  );

  const missingDefaults = useMemo<Activity[]>(() => {
    const have = new Set(activities.map((a) => nameKey(a.naam)));
    return DEFAULT_ACTIVITIES.filter((d) => !have.has(nameKey(d.naam)));
  }, [activities]);

  const addMissingDefaults = useCallback(async (): Promise<number> => {
    const current = listRef.current;
    const have = new Set(current.map((a) => nameKey(a.naam)));
    if (!DEFAULT_ACTIVITIES.some((d) => !have.has(nameKey(d.naam)))) return 0;
    const saved = await persist(mergeActivities(current, DEFAULT_ACTIVITIES));
    return Math.max(0, saved.length - current.length);
  }, [persist]);

  const api = useMemo<ActivitiesApi>(
    () => ({
      activities,
      loading,
      importActivities,
      incrementScore,
      resetToDefaults,
      customized,
      addActivity,
      updateActivity,
      deleteActivity,
      missingDefaults,
      addMissingDefaults,
    }),
    [activities, loading, importActivities, incrementScore, resetToDefaults, customized, addActivity, updateActivity, deleteActivity, missingDefaults, addMissingDefaults],
  );

  return <ActivitiesContext.Provider value={api}>{children}</ActivitiesContext.Provider>;
}

export function useActivities(): ActivitiesApi {
  const ctx = useContext(ActivitiesContext);
  if (!ctx) throw new Error('useActivities moet binnen <ActivitiesProvider> gebruikt worden');
  return ctx;
}
